<script>
import { EventBus } from "@/libraries/EventBus";
import HalfDashCard from "@/components/Cards/HalfDashCard";
import {
    CaseCategory,
    CaseStatus,
    CaseStatusOrder,
    CaseSources,
    CaseFinalizedTypes
} from "@/constants/TurbineStops.const";
import TurbineStopService from "@/services/TurbineStopService";
import VTurbineStopForm from "@/components/Forms/VTurbineStopForm.vue";
import VTurbineSearch from "@/components/VTurbineSearch.vue";
import Messages from "@/mixins/Messages";
import VMicroCard from "@/components/Cards/VMicroCard.vue";
import BaseSwitch from "@/components/BaseSwitch";
import TriState from "./components/TriState.vue";

const defaultSelectedStatuses = [0, 1, 2, 3];

export default {
    components: {
        HalfDashCard,
        VTurbineStopForm,
        VTurbineSearch,
        VMicroCard,
        BaseSwitch,
        TriState
    },
    mixins: [Messages],
    data() {
        return {
            loading: true,
            turbines: [],
            turbineStops: [],
            showForm: false,
            paginated: true,
            meta: {},
            caseSources: CaseSources,
            caseCategories: CaseCategory,
            caseStatuses: CaseStatus,
            selectedStatuses: defaultSelectedStatuses,
            selectedTurbineIds: [],
            showSecData: false,
            showCommentModal: false,
            modalContext: "",
            modalContextTitle: "",
            modalScope: "",
            modalItem: {},
            modalFieldKey: "",
            search: "",
            _searchTimer: null,
            search_mode: 0,
            _reloadTimer: null,
            dataVersion: ""
        };
    },
    computed: {
        finalized_types() {
            return CaseFinalizedTypes;
        },
        statusOrder() {
            return CaseStatusOrder;
        },
        currentExportUrl: function() {
            let params = this.$buildParams();
            delete params.sort;
            delete params.direction;
            delete params.page;
            delete params.limit;

            let url = new URL(
                location.protocol + "//" + location.host + "/v1/sl-cases"
            );
            url.searchParams.append("csv", 1);

            this.selectedStatuses.forEach(function(value) {
                url.searchParams.append("status[]", value);
            });
            for (let param in params) {
                if (Array.isArray(params[param])) {
                    params[param].forEach(function(value) {
                        url.searchParams.append(param + "[]", value);
                    });
                } else {
                    url.searchParams.append(param, params[param]);
                }
            }

            return url.href;
        }
    },
    watch: {
        selectedStatuses() {
            this.fetchData();
        },
        search(val) {
            clearTimeout(this._searchTimer);
            this._searchTimer = setTimeout(() => {
                this.fetchData();
            }, 300);
        },
        search_mode(val) {
            console.log("Search mode trigger", val);
            this.fetchData();
        }
    },
    async created() {
        // Since this page has a very wide table, we'll force the sidebar to be minimized:
        EventBus.$emit("force-minimized-sidebar");

        this.$apiEnableWatcher();
        this.apiPaginator.sort = "id";
        this.apiPaginator.direction = "desc";
        this.apiPaginator.limit = "100";

        await this.fetchData();
        this.loading = false;
        this._reloadTimer = setInterval(() => {
            this.fetchData();
        }, 1000 * 120);
    },
    beforeDestroy() {
        clearInterval(this._reloadTimer);
        EventBus.$emit("remove-forced-minimized-sidebar");
    },
    methods: {
        modalize(title, fieldKey, item) {
            this.showCommentModal = true;
            this.modalContext = item[fieldKey];
            this.modalContextTitle = title;
            this.modalFieldKey = fieldKey;
            this.modalItem = item;
        },
        editModalize(item, fieldKey) {
            this.showCommentModal = false;
            this.setContext(item, fieldKey);
        },
        // Get turbine stop cases.
        async fetchData() {
            try {
                let params = this.$buildParams();
                params.search = this.search;
                params.search_mode = this.search_mode;
                params.status = this.selectedStatuses;
                params.sl_turbine_id = this.selectedTurbineIds;
                const { data } = await TurbineStopService.GET(params);
                this.turbineStops = data.data;
                this.meta = data.meta;
                this.dataVersion = new Date();
            } catch (e) {
                console.log("Fetch error:", e);
            }
        },
        deleteTurbineStop(id) {
            this.$askDeleteQuestion(
                this.$t("service.confirm_delete"),
                async () => {
                    try {
                        await TurbineStopService.DELETE(id);
                        this.fetchData();

                        this.$notify({
                            message: this.$t("service.deleted"),
                            timeout: 5000,
                            icon: "far fa-check",
                            type: "success"
                        });
                    } catch (e) {
                        console.log("Deletion error:", e);
                    }
                }
            );
        },
        setContext(ctx, scope = "") {
            this.context = ctx;
            this.modalScope = scope;
            this.showForm = true;
        },
        closeReload() {
            this.fetchData();
            this.showForm = false;
        },
        selectTurbines(turbines) {
            this.selectedTurbineIds = [];
            for (const item of turbines) {
                this.selectedTurbineIds.push(item.value);
            }
            this.fetchData();
        },
        /**
         * Handle update from the base-switch / is contacted
         * @param item
         * @param event
         */
        async switchSwitch(field, item, event) {
            const entity = {
                id: item.id,
                [field]: event
            };
            try {
                await TurbineStopService.PUT(entity);
            } catch (e) {
                console.log("switch update error", e);
            }
        },
        triggerSearch(d) {
            console.log(d);
        },
        /**
         * Red alarm
         * @param item
         */
        hasAlarm(item) {
            const alarm = this.meta.alarms[item.sl_turbine_id];
            // eslint-disable-next-line eqeqeq
            return alarm && alarm.error_code == item.error_code;
        },
        hasOrangeAlarm(item) {
            if (this.hasAlarm(item)) {
                return false;
            }

            const orange = this.meta.orange_alarms[item.sl_turbine_id];

            if (parseInt(orange) > 0) {
                return true;
            } else {
                false;
            }
        }
    }
};
</script>

<template>
    <div>
        <div class="v-projects">
            <base-header class="pb-1" type="">
                <bread-bag>
                    <base-button
                        @click="setContext({})"
                        type="primary"
                        size="sm"
                    >
                        <i class="far fa-plus"></i>
                        {{ $t("service.create_case") }}
                    </base-button>
                    <a :href="currentExportUrl" class="btn btn-primary btn-sm">
                        <i class="far fa-file-csv"></i>
                        {{ $t("service.export") }}
                    </a>
                </bread-bag>
            </base-header>
        </div>

        <div class="container-fluid">
            <div class="row card-wrapper mb-4">
                <div class="col-12">
                    <v-loader :loading="loading" v-if="loading" />
                    <template v-else>
                        <div class="row">
                            <div
                                v-for="(item, key) in meta.stats"
                                :key="key"
                                class="col-12 col-md"
                            >
                                <half-dash-card
                                    :title="item.title"
                                    :icon="item.icon"
                                    :value="item.value"
                                    class="box-stoplist"
                                    :class="
                                        item.status == 9 ? 'bg-finalized' : ''
                                    "
                                />
                            </div>
                            <div class="col-12 col-md">
                                <half-dash-card
                                    :title="
                                        $t('service.top_ab_service_box_title')
                                    "
                                    icon="far fa-chart-pie-alt"
                                    :value="meta.finalized_types.total_ab"
                                    class="box-stoplist"
                                ></half-dash-card>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-2 offset-7">
                                <half-dash-card
                                    :title="meta.finalized_types[1]['name']"
                                    icon="far fa-router"
                                    :value="meta.finalized_types[1]['count']"
                                    class="box-stoplist bg-finalized"
                                ></half-dash-card>
                            </div>
                            <div class="col-2">
                                <half-dash-card
                                    :title="meta.finalized_types[2]['name']"
                                    icon="far fa-car"
                                    :value="meta.finalized_types[2]['count']"
                                    class="box-stoplist bg-finalized"
                                ></half-dash-card>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div class="row card-wrapper">
                <div class="col-3">
                    <v-turbine-search @choose-item="selectTurbines" />
                </div>
                <div class="col-4">
                    <div class="d-flex align-items-center">
                        <input
                            type="text"
                            autocomplete="no"
                            class="form-control sl-search"
                            v-model="search"
                            :placeholder="$t('service.placeholder_search')"
                        />
                        <div style="width: 200px;" class="ml-2">
                            <input
                                type="checkbox"
                                v-model="search_mode"
                                true-value="1"
                                false-value="0"
                            />
                            -
                            <span class="small">{{
                                $t("service.exact_search")
                            }}</span>
                        </div>
                    </div>
                </div>
                <div class="col-3">
                    <FormulateInput
                        class="sl-fi-search"
                        type="multiselect"
                        :placeholder="$t('service.category')"
                        v-model="apiSearchFilter.entry_category"
                        multiple
                        :options="caseCategories"
                    />
                </div>
                <div class="col-2">
                    <FormulateInput
                        class="sl-fi-search"
                        :placeholder="$t('service.source')"
                        type="multiselect"
                        v-model="apiSearchFilter.source"
                        multiple
                        :options="caseSources"
                    />
                </div>
            </div>

            <div class="row card-wrapper">
                <div class="col-12">
                    <card body-classes="px-0">
                        <v-loader :loading="loading" v-if="loading" />
                        <template v-else>
                            <div class="row pr-4 pb-3 pl-4">
                                <div class="col-6">
                                    <label
                                        v-for="(item, key) in statusOrder"
                                        :key="key"
                                        class="mr-4"
                                    >
                                        <input
                                            v-model="selectedStatuses"
                                            type="checkbox"
                                            :value="item"
                                        />
                                        {{ caseStatuses[item] }}
                                    </label>
                                </div>
                                <div class="col-6 text-right small text-muted">
                                    {{ $t("service.search_note") }}
                                    <div>
                                        {{ $t("service.data_read_on") }}:
                                        {{ $d(dataVersion, "long") }}
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="!turbineStops.length"
                                class="container-fluid"
                            >
                                <div class="row card-wrapper">
                                    <div class="col-12">
                                        <div class="alert alert-warning">
                                            {{ $t("service.no_cases") }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else>
                                <div class="aoverflow-auto">
                                    <v-table
                                        class="table-mg table table-sm table-sl-cases table-bordered table-striped"
                                    >
                                        <v-thead>
                                            <v-tr>
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="so_nr"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.so_nr_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="entry_category"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.case_category_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="SlTurbines.serial_number"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.serial_number_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="SlTurbines.name"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.turbine_name_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="SlTurbines.address"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.address_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >

                                                <v-th v-if="showSecData"
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="Users.name"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.user_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="SupervisorUsers.name"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.supervisor_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >

                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="error_code"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.error_code_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th class="sl-col-error-text"
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="error_text"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.error_text_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >

                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="car"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.car_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="status"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.status_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    class="text-center sl-col-comments"
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="comments"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.comments_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="start_date"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.start_date_col"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th
                                                    ><v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        name="exp_repair_date"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                "service.exp_repair_date_short"
                                                            )
                                                        }}
                                                    </v-sorter></v-th
                                                >
                                                <v-th>&nbsp;</v-th>
                                            </v-tr>
                                        </v-thead>

                                        <v-tbody>
                                            <v-tr
                                                v-for="(item,
                                                i) in turbineStops"
                                                :key="item.id"
                                                :class="
                                                    hasAlarm(item)
                                                        ? 'highlight-condition'
                                                        : hasOrangeAlarm(item)
                                                        ? 'orange-condition'
                                                        : ''
                                                "
                                            >
                                                <v-td
                                                    ><span
                                                        class="m-click"
                                                        @click="
                                                            setContext(
                                                                item,
                                                                'so_nr'
                                                            )
                                                        "
                                                        ><template
                                                            v-if="item.so_nr"
                                                            >{{
                                                                item.so_nr
                                                            }}</template
                                                        ><template v-else
                                                            >---</template
                                                        ></span
                                                    >
                                                    <div class="small">
                                                        {{
                                                            $t(
                                                                "service.contacted"
                                                            )
                                                        }}:
                                                        <base-switch
                                                            @input="
                                                                switchSwitch(
                                                                    'is_contacted',
                                                                    item,
                                                                    $event
                                                                )
                                                            "
                                                            :value="
                                                                item.is_contacted
                                                            "
                                                            :on-text="
                                                                $t(
                                                                    'service.yes'
                                                                )
                                                            "
                                                            :off-text="
                                                                $t('service.no')
                                                            "
                                                            :use-numeric="true"
                                                        ></base-switch></div
                                                ></v-td>
                                                <v-td
                                                    ><span
                                                        class="m-click"
                                                        @click="
                                                            setContext(
                                                                item,
                                                                'entry_category'
                                                            )
                                                        "
                                                        >{{
                                                            caseCategories[
                                                                item
                                                                    .entry_category
                                                            ]
                                                        }}</span
                                                    >
                                                    <div class="small">
                                                        {{
                                                            $t(
                                                                "service.driving"
                                                            )
                                                        }}:
                                                        <base-switch
                                                            @input="
                                                                switchSwitch(
                                                                    'is_driving',
                                                                    item,
                                                                    $event
                                                                )
                                                            "
                                                            :value="
                                                                item.is_driving
                                                            "
                                                            :on-text="
                                                                $t(
                                                                    'service.yes'
                                                                )
                                                            "
                                                            :off-text="
                                                                $t('service.no')
                                                            "
                                                            :use-numeric="true"
                                                        ></base-switch></div
                                                ></v-td>
                                                <v-td
                                                    >{{
                                                        item.sl_turbine
                                                            .serial_number
                                                    }}

                                                    <div class="text-muted">
                                                        {{
                                                            item.sl_turbine
                                                                .wm_type
                                                        }}
                                                    </div>
                                                </v-td>
                                                <v-td
                                                    ><span
                                                        class="m-click"
                                                        @click="
                                                            setContext(item)
                                                        "
                                                        >{{
                                                            item.sl_turbine.name
                                                        }}</span
                                                    >
                                                    <div
                                                        class="text-muted text-uppercase mt-1"
                                                    >
                                                        {{
                                                            item.sl_turbine
                                                                .source
                                                        }}
                                                    </div>
                                                    <div>
                                                        <span
                                                            class="text-danger text-bold mr-1"
                                                            v-if="
                                                                hasAlarm(item)
                                                            "
                                                        >
                                                            <i
                                                                class="far fa-siren-on"
                                                            ></i>
                                                            {{
                                                                meta.alarms[
                                                                    item
                                                                        .sl_turbine_id
                                                                ].case_count
                                                            }}
                                                        </span>
                                                        <span
                                                            class="text-muted"
                                                        >
                                                            <i
                                                                class="far fa-sigma"
                                                            ></i>
                                                            {{
                                                                meta.freqs[
                                                                    item
                                                                        .sl_turbine_id
                                                                ]
                                                            }}
                                                        </span>
                                                    </div></v-td
                                                >
                                                <v-td
                                                    ><span
                                                        class="m-click"
                                                        @click="
                                                            setContext(item)
                                                        "
                                                        >{{
                                                            item.sl_turbine
                                                                .address
                                                        }}
                                                        <div
                                                            class="text-bold"
                                                            v-if="
                                                                item.sl_turbine
                                                                    .map_link !==
                                                                    ''
                                                            "
                                                        >
                                                            <a
                                                                :href="
                                                                    item
                                                                        .sl_turbine
                                                                        .map_link
                                                                "
                                                                target="_blank"
                                                                ><i
                                                                    class="far fa-map-pin"
                                                                ></i>
                                                                {{
                                                                    $t(
                                                                        "service.map"
                                                                    )
                                                                }}</a
                                                            >
                                                        </div></span
                                                    ></v-td
                                                >

                                                <v-td
                                                    ><span
                                                        class="m-click text-uppercase"
                                                        @click="
                                                            setContext(
                                                                item,
                                                                'supervisor_user_id'
                                                            )
                                                        "
                                                        ><template
                                                            v-if="
                                                                'supervisor_user' in
                                                                    item
                                                            "
                                                            >{{
                                                                item
                                                                    .supervisor_user
                                                                    .initials
                                                            }}</template
                                                        ><template v-else
                                                            >---</template
                                                        ></span
                                                    >
                                                    <div class="small">
                                                        {{
                                                            $t(
                                                                "service.xtra_task"
                                                            )
                                                        }}
                                                    </div>

                                                    <tri-state
                                                        :value="item.extra_task"
                                                        @change="
                                                            switchSwitch(
                                                                'extra_task',
                                                                item,
                                                                $event
                                                            )
                                                        "
                                                    />
                                                </v-td>

                                                <v-td
                                                    ><span
                                                        class="m-click"
                                                        @click="
                                                            setContext(
                                                                item,
                                                                'error_code'
                                                            )
                                                        "
                                                        ><template
                                                            v-if="
                                                                item.error_code
                                                            "
                                                            >{{
                                                                item.error_code
                                                            }}</template
                                                        ><template v-else
                                                            >---</template
                                                        >
                                                    </span></v-td
                                                >
                                                <v-td class=""
                                                    ><template
                                                        v-if="item.error_text"
                                                    >
                                                        <v-newlines
                                                            @click.native="
                                                                modalize(
                                                                    $t(
                                                                        'service.error_text'
                                                                    ),
                                                                    'error_text',
                                                                    item
                                                                )
                                                            "
                                                            class="m-click"
                                                            :text="
                                                                item.error_text_excerpt
                                                            "
                                                        />
                                                    </template>
                                                    <template v-else>
                                                        ---
                                                    </template>
                                                </v-td>
                                                <v-td
                                                    ><span
                                                        class="m-click"
                                                        @click="
                                                            setContext(
                                                                item,
                                                                'car'
                                                            )
                                                        "
                                                        ><template
                                                            v-if="item.car"
                                                            >{{
                                                                item.car
                                                            }}</template
                                                        ><template v-else
                                                            >---</template
                                                        ></span
                                                    ></v-td
                                                >
                                                <v-td
                                                    ><span
                                                        class="m-click"
                                                        @click="
                                                            setContext(
                                                                item,
                                                                'status'
                                                            )
                                                        "
                                                        >{{
                                                            caseStatuses[
                                                                item.status
                                                            ]
                                                        }}</span
                                                    >
                                                    <template
                                                        v-if="
                                                            item.status == 9 &&
                                                                parseInt(
                                                                    item.finalized_type
                                                                ) > 0
                                                        "
                                                    >
                                                        <hr
                                                            style="margin-bottom: 5px; margin-top: 5px;"
                                                        />
                                                        <span
                                                            style="font-style: italic;"
                                                            >{{
                                                                finalized_types[
                                                                    item
                                                                        .finalized_type
                                                                ]
                                                            }}</span
                                                        >
                                                    </template>
                                                </v-td>
                                                <v-td class="text-center">
                                                    <template
                                                        v-if="item.comments"
                                                        ><i
                                                            class="far fa-comments fa-fw fa-lg m-click"
                                                            @click="
                                                                modalize(
                                                                    $t(
                                                                        'service.comments'
                                                                    ),
                                                                    'comments',
                                                                    item
                                                                )
                                                            "
                                                        ></i
                                                    ></template>
                                                    <template v-else
                                                        >---</template
                                                    >
                                                </v-td>
                                                <v-td>
                                                    <span
                                                        class="m-click"
                                                        @click="
                                                            setContext(
                                                                item,
                                                                'start_date'
                                                            )
                                                        "
                                                    >
                                                        <template
                                                            v-if="
                                                                item.start_date
                                                            "
                                                        >
                                                            {{
                                                                $d(
                                                                    new Date(
                                                                        item.start_date
                                                                    ),
                                                                    "short"
                                                                )
                                                            }} </template
                                                        ><template v-else
                                                            >---</template
                                                        ></span
                                                    >
                                                </v-td>
                                                <v-td
                                                    ><span
                                                        class="m-click"
                                                        @click="
                                                            setContext(
                                                                item,
                                                                'exp_repair_date'
                                                            )
                                                        "
                                                    >
                                                        <template
                                                            v-if="
                                                                item.exp_repair_date
                                                            "
                                                        >
                                                            {{
                                                                $d(
                                                                    new Date(
                                                                        item.exp_repair_date
                                                                    ),
                                                                    "short"
                                                                )
                                                            }}
                                                        </template></span
                                                    >
                                                </v-td>
                                                <v-td
                                                    style="width: 50px; padding: 0px;"
                                                >
                                                    <base-button
                                                        :title="
                                                            $t(
                                                                'last_modified'
                                                            ) +
                                                                ': ' +
                                                                $d(
                                                                    new Date(
                                                                        item.modified
                                                                    ),
                                                                    'longFull'
                                                                )
                                                        "
                                                        @click="
                                                            setContext(item)
                                                        "
                                                        class="mb-2 "
                                                        type="primary"
                                                        size="sm"
                                                    >
                                                        <i
                                                            class="far fa-fw fa-pencil"
                                                        ></i>
                                                    </base-button>
                                                    <base-button
                                                        @click="
                                                            deleteTurbineStop(
                                                                item.id
                                                            )
                                                        "
                                                        class=""
                                                        type="danger"
                                                        outline
                                                        size="sm"
                                                    >
                                                        <i
                                                            class="far fa-fw fa-trash"
                                                        ></i>
                                                    </base-button>
                                                </v-td>
                                            </v-tr>
                                        </v-tbody>
                                    </v-table>
                                </div>

                                <div class="paginator" v-if="paginated">
                                    <base-pagination
                                        :page-count="
                                            meta.pagination.total_pages
                                        "
                                        :per-page="meta.pagination.per_page"
                                        :value="meta.pagination.current_page"
                                        @input="$handlePagination($event)"
                                    />
                                </div>
                            </div>
                        </template>
                    </card>
                </div>
            </div>
        </div>

        <portal to="modals">
            <modal
                v-if="showCommentModal"
                size="lg"
                :show="true"
                @close="showCommentModal = false"
                class="asset-modal"
                @edit-comments="editComments"
            >
                <template slot="header">{{ modalContextTitle }}</template>
                <template slot="default"
                    ><v-newlines :text="modalContext" />

                    <div class="mt-3 text-right">
                        <base-button
                            type="primary"
                            size="sm"
                            outline
                            @click="editModalize(modalItem, modalFieldKey)"
                            >{{ $t("edit") }}</base-button
                        >
                    </div></template
                >
            </modal>
            <v-turbine-stop-form
                v-if="showForm"
                @close="showForm = false"
                @close-reload="closeReload"
                :context="context"
                :scope="modalScope"
            />
        </portal>
    </div>
</template>

<style lang="scss">
.sl-fi-search {
    &.form-group {
        margin-bottom: 0px;
    }

    .vs__dropdown-toggle {
        height: 34px !important;
        min-height: 34px !important;
        border: 1px solid rgb(184, 193, 191);
    }
}

.bg-finalized {
    background: $gray-100;
}

.sl-search {
    height: 33px;
}
.table-sl-cases {
    .highlight-condition {
        td {
            background-color: #faeaea !important;

            border-top: 3px solid $danger !important;
            border-bottom: 3px solid $danger !important;
            border-left: 1px solid $gray-400;
            border-right: 1px solid $gray-400;

            &:first-child {
                border-left: 3px solid $danger !important;
            }

            &:last-child {
                border-right: 3px solid $danger !important;
            }
        }
    }

    .orange-condition {
        td {
            background-color: rgb(255, 230, 183) !important;

            border-top: 3px solid orange !important;
            border-bottom: 3px solid orange !important;
            border-left: 1px solid $gray-400;
            border-right: 1px solid $gray-400;

            &:first-child {
                border-left: 3px solid $danger !important;
            }

            &:last-child {
                border-right: 3px solid $danger !important;
            }
        }
    }

    thead th {
        position: -webkit-sticky; /* Safari */
        position: sticky;
        top: 0; /* sticks to top of viewport */
        z-index: 200; /* keep header above rows / other elements */
        background-clip: padding-box;
        box-shadow: 0 2px 3px rgba(0, 0, 0, 0.06); /* optional visual separation */
    }

    td {
        padding: 4px 8px !important;
        font-size: 11px !important;
    }

    th {
        padding: 4px 8px !important;
        .v-sorter__elem {
            font-size: 11px !important;
        }
    }

    .sl-col-comments {
    }

    .sl-col-error-text {
        min-width: 200px;
    }
}

.box-stoplist {
    .card-body {
        padding-top: 12px !important;
        padding-bottom: 8px !important;
    }
    .dash-card__icon {
        font-size: 24px;
    }

    h5.card-title {
        font-size: 10px !important;
    }

    h3,
    small {
        font-size: 14px !important;
    }
}
</style>
<style scoped>
.fixed-width {
}
</style>
